import ClassPresentation from "../../Components/ClassPresentation";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiImage,
  FiLayers,
  FiMapPin,
  FiRefreshCw,
  FiSearch,
  FiSliders,
  FiUsers,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import MenuLateral from "../../Components/MenuLateral";
import CadastroPageHeader, {
  CadastroPanelHeading,
} from "../../Components/CadastroPageHeader";
import { LoginContext } from "../../context/LoginContext";
import api from "../../services/api";
import AuthenticatedMedia from "../../Components/AuthenticatedMedia";
import { USER_ROLES } from "../../auth/session";
import "../../styles/cadastro-pages.css";
import "./index.css";

const PAGE_SIZE_OPTIONS = [6, 12, 24];

const INITIAL_FILTERS = Object.freeze({
  search: "",
  status: "",
  startDate: "",
  endDate: "",
  sort: "recent",
});

const SORT_OPTIONS = Object.freeze({
  recent: { sortBy: "eventDate", sortOrder: "DESC", label: "Mais recentes" },
  oldest: { sortBy: "eventDate", sortOrder: "ASC", label: "Mais antigos" },
  nameAsc: { sortBy: "name", sortOrder: "ASC", label: "Nome: A–Z" },
  nameDesc: { sortBy: "name", sortOrder: "DESC", label: "Nome: Z–A" },
});

const EVENT_STATUS_LABELS = Object.freeze({
  scheduled: "Agendado",
  in_progress: "Em andamento",
  completed: "Concluído",
  cancelled: "Cancelado",
});

const EVENT_STATUS_ICONS = Object.freeze({
  scheduled: FiCalendar,
  in_progress: FiClock,
  completed: FiCheckCircle,
  cancelled: FiAlertCircle,
});

const EVENT_TYPE_LABELS = Object.freeze({
  graduation: "Formatura",
  school_memory: "Memória escolar",
  personal_photos: "Fotos pessoais",
  other: "Outro tipo",
});

const PUBLICATION_STATUS_LABELS = Object.freeze({
  PUBLISHED: "Publicado",
  UNPUBLISHED: "Ainda não publicado",
});

const formatarErro = (error) => {
  if (!error) return "Não foi possível carregar os eventos.";

  const responseData = error.response?.data;

  if (Array.isArray(responseData?.message)) {
    return responseData.message.join(", ");
  }

  if (responseData?.message) return responseData.message;
  return error.message || "Não foi possível comunicar com a API.";
};

const formatarData = (value, options = { dateStyle: "long" }) => {
  if (!value) return "Data não informada";

  const [datePart] = String(value).split("T");
  const [year, month, day] = datePart.split("-").map(Number);

  if (!year || !month || !day) return "Data não informada";

  return new Intl.DateTimeFormat("pt-BR", options).format(
    new Date(year, month - 1, day),
  );
};

const formatarDataDoCard = (value) => {
  if (!value) return { day: "--", month: "---", full: "Data não informada" };

  const [datePart] = String(value).split("T");
  const [year, month, day] = datePart.split("-").map(Number);

  if (!year || !month || !day) {
    return { day: "--", month: "---", full: "Data não informada" };
  }

  const date = new Date(year, month - 1, day);

  return {
    day: new Intl.DateTimeFormat("pt-BR", { day: "2-digit" }).format(date),
    month: new Intl.DateTimeFormat("pt-BR", { month: "short" })
      .format(date)
      .replace(".", ""),
    full: formatarData(value),
  };
};

const getEventList = (payload) => {
  if (Array.isArray(payload)) {
    return {
      data: payload,
      total: payload.length,
      page: 1,
      limit: payload.length || 1,
    };
  }

  const data = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.events)
      ? payload.events
      : Array.isArray(payload?.items)
        ? payload.items
        : [];

  return {
    data,
    total: Number(payload?.total ?? data.length),
    page: Number(payload?.page ?? 1),
    limit: Number(payload?.limit ?? 12),
  };
};

const getInstitutionNames = (eventClasses = []) => {
  const names = Array.from(
    new Set(eventClasses.map((item) => item.institution?.name).filter(Boolean)),
  );

  return names.length ? names.join(", ") : "Instituição não informada";
};

const getLocationLabel = (event) =>
  event.venueName ||
  event.location ||
  [event.venueCity, event.venueUf].filter(Boolean).join(" / ") ||
  "Local não informado";

const getEventStatusLabel = (status) =>
  EVENT_STATUS_LABELS[status] || "Situação não informada";

const getPublicationStatusLabel = (status) =>
  PUBLICATION_STATUS_LABELS[status] || "Publicação não informada";

const getPublicationStatusTone = (status) =>
  status === "PUBLISHED" ? "published" : "unpublished";

const isCanceledRequest = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

function EventCard({ event, onOpen }) {
  const date = formatarDataDoCard(event.eventDate);
  const StatusIcon = EVENT_STATUS_ICONS[event.status] || FiCalendar;
  const classes = event.classes || [];
  const classLabel = classes.length
    ? `${classes.length} ${classes.length === 1 ? "turma" : "turmas"}`
    : "Sem turmas";
  const hasPeopleQuantity =
    event.peopleQuantity !== null &&
    event.peopleQuantity !== undefined &&
    event.peopleQuantity !== "";
  const peopleLabel = hasPeopleQuantity && Number.isFinite(Number(event.peopleQuantity))
    ? `${event.peopleQuantity} ${Number(event.peopleQuantity) === 1 ? "pessoa" : "pessoas"}`
    : "Quantidade não informada";

  return (
    <article className="evento-card">
      <div className="evento-card-media">
        {event.coverPath ? (
          <AuthenticatedMedia
            path={event.coverPath}
            alt={`Capa do evento ${event.name}`}
            className="evento-card-cover"
            loading="lazy"
            decoding="async"
            fallback={
              <div className="evento-card-cover-placeholder">
                <FiImage aria-hidden="true" />
                <span>Capa indisponível</span>
              </div>
            }
          />
        ) : (
          <div className="evento-card-cover-placeholder">
            <FiImage aria-hidden="true" />
            <span>Sem capa cadastrada</span>
          </div>
        )}

        <div className="evento-date-stamp" aria-label={`Data: ${date.full}`}>
          <strong>{date.day}</strong>
          <span>{date.month}</span>
        </div>

        <span className="evento-type-tag">
          {EVENT_TYPE_LABELS[event.eventType] || "Evento"}
        </span>

        <span className={`evento-status-pill evento-status-${event.status || "unknown"}`}>
          <StatusIcon aria-hidden="true" />
          {getEventStatusLabel(event.status)}
        </span>
      </div>

      <div className="evento-card-content">
        <span className="evento-card-institution">{getInstitutionNames(classes)}</span>
        <h3>{event.name || "Evento sem nome"}</h3>

        <div className="evento-card-facts" aria-label="Resumo do evento">
          <span>
            <FiMapPin aria-hidden="true" />
            {getLocationLabel(event)}
          </span>
          <span>
            <FiLayers aria-hidden="true" />
            {classLabel}
          </span>
          <span>
            <FiUsers aria-hidden="true" />
            {peopleLabel}
          </span>
        </div>

        <div className={`evento-card-publication evento-publication-${getPublicationStatusTone(event.publicationStatus)}`}>
          <span>Fotos do evento</span>
          <strong>{getPublicationStatusLabel(event.publicationStatus)}</strong>
        </div>
      </div>

      <footer className="evento-card-footer">
        <span>{date.full}</span>
        <button type="button" onClick={() => onOpen(event.id)}>
          Abrir evento
          <FiArrowRight aria-hidden="true" />
        </button>
      </footer>
    </article>
  );
}

function EventCardSkeleton() {
  return (
    <div className="evento-card evento-card-skeleton" aria-hidden="true">
      <div className="evento-skeleton-media" />
      <div className="evento-skeleton-content">
        <span />
        <strong />
        <i />
        <i />
        <i />
      </div>
      <div className="evento-skeleton-footer" />
    </div>
  );
}

function Pagination({ page, totalPages, total, limit, onPageChange }) {
  if (total <= limit) return null;

  const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
  const firstItem = (page - 1) * limit + 1;
  const lastItem = Math.min(page * limit, total);

  return (
    <nav className="eventos-pagination" aria-label="Paginação dos eventos">
      <span>
        Exibindo <strong>{firstItem}–{lastItem}</strong> de <strong>{total}</strong>
      </span>

      <div className="eventos-pagination-controls">
        <button
          type="button"
          className="eventos-page-button eventos-page-button-previous"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          aria-label="Ir para a página anterior"
        >
          Anterior
        </button>

        {pages.map((pageNumber) => (
          <button
            type="button"
            key={pageNumber}
            className={`eventos-page-button ${pageNumber === page ? "is-current" : ""}`}
            onClick={() => onPageChange(pageNumber)}
            aria-current={pageNumber === page ? "page" : undefined}
          >
            {pageNumber}
          </button>
        ))}

        <button
          type="button"
          className="eventos-page-button eventos-page-button-next"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          aria-label="Ir para a próxima página"
        >
          Próxima
        </button>
      </div>
    </nav>
  );
}

function Eventos() {
  const { LoggedUser } = useContext(LoginContext);
  const role = LoggedUser?.role;
  const isStudentUser = role === USER_ROLES.USER;
  const navigate = useNavigate();

  const [eventos, setEventos] = useState([]);
  const [myPhotoGroups, setMyPhotoGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 12 });
  const [errorMessage, setErrorMessage] = useState("");
  const [studentMessage, setStudentMessage] = useState("");

  const activeFilterCount = useMemo(
    () =>
      [
        appliedFilters.search,
        appliedFilters.status,
        appliedFilters.startDate,
        appliedFilters.endDate,
      ].filter(Boolean).length,
    [appliedFilters],
  );

  const totalPages = Math.max(1, Math.ceil(pagination.total / pageSize));

  const carregarMinhasFotos = async (signal) => {
    setLoading(true);
    setStudentMessage("");

    try {
      const response = await api.get("/photos/me", {
        params: { page: 1, limit: 100 },
        signal,
      });

      const groups = response.data?.groups || [];
      setMyPhotoGroups(groups);

      if (groups.length === 0) {
        setStudentMessage("Nenhuma foto reconhecida foi encontrada para esta conta.");
      }
    } catch (error) {
      if (isCanceledRequest(error)) return;

      const message = formatarErro(error);
      setMyPhotoGroups([]);
      setStudentMessage(
        error.response?.status === 403
          ? message
          : "Não foi possível carregar suas fotos. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isStudentUser) return undefined;

    const controller = new AbortController();
    carregarMinhasFotos(controller.signal);

    return () => controller.abort();
    // A troca de papel deve iniciar uma nova carga completa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudentUser]);

  useEffect(() => {
    if (isStudentUser) return undefined;

    const controller = new AbortController();
    let active = true;
    const sortOption = SORT_OPTIONS[appliedFilters.sort] || SORT_OPTIONS.recent;
    const params = {
      page,
      limit: pageSize,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
    };

    if (appliedFilters.search.trim()) params.search = appliedFilters.search.trim();
    if (appliedFilters.status) params.status = appliedFilters.status;
    if (appliedFilters.startDate) params.startDate = appliedFilters.startDate;
    if (appliedFilters.endDate) params.endDate = appliedFilters.endDate;

    const carregarEventos = async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        const response = await api.get("/events", {
          params,
          signal: controller.signal,
        });
        if (!active) return;

        const result = getEventList(response.data);
        const eventsWithCover = result.data.map((event) => ({
          ...event,
          coverPath: event.coverPhotoMedia?.path || null,
        }));

        setEventos(eventsWithCover);
        setPagination({
          total: result.total,
          page: result.page,
          limit: result.limit,
        });
      } catch (error) {
        if (!active || isCanceledRequest(error)) return;

        setEventos([]);
        setPagination({ total: 0, page: 1, limit: pageSize });
        setErrorMessage(formatarErro(error));
      } finally {
        if (active) setLoading(false);
      }
    };

    carregarEventos();

    return () => {
      active = false;
      controller.abort();
    };
  }, [appliedFilters, isStudentUser, page, pageSize]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleFilterSubmit = (event) => {
    event.preventDefault();

    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setErrorMessage("A data inicial deve ser anterior ou igual à data final.");
      return;
    }

    setErrorMessage("");
    setPage(1);
    setAppliedFilters({ ...filters, search: filters.search.trim() });
  };

  const clearFilters = () => {
    setErrorMessage("");
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const openEvent = (eventId) => {
    navigate(`/Evento/Detalhe/${eventId}`);
  };

  if (isStudentUser) {
    return (
      <div className="pagina-cad cadastro-shell eventos-page">
        <MenuLateral />

        <main className="conteudo-cad">
          <CadastroPageHeader
            eyebrow="Área autenticada"
            title="Minhas fotos"
            description="Aqui você encontra apenas as fotos reconhecidas para o seu perfil."
            icon={FiImage}
            context="Conteúdo autorizado"
            contextDetail="Fotos vinculadas ao seu acesso"
          />

          {loading && (
            <p className="eventos-loading" role="status" aria-live="polite">
              Carregando suas fotos…
            </p>
          )}

          {!loading && studentMessage && (
            <div className="eventos-empty-state" role="status">
              <FiImage aria-hidden="true" />
              <strong>{studentMessage}</strong>
            </div>
          )}

          <div className="photo-groups">
            {myPhotoGroups.map((group) => (
              <section
                key={`${group.classId || "sem-turma"}-${group.eventId}`}
                className="photo-group-card"
              >
                <div className="photo-group-header">
                  <div>
                    <strong>{group.eventName}</strong>
                    <ClassPresentation schoolClass={group} />
                  </div>
                  <small>
                    {formatarData(group.eventDate, { dateStyle: "medium" })} • {group.photos?.length || 0} foto(s)
                  </small>
                </div>

                <div className="photo-group-grid">
                  {(group.photos || []).map((item) => (
                    <div key={item.matchId} className="photo-thumb-card">
                      <AuthenticatedMedia
                        path={item.photo?.media?.thumbnail?.path}
                        alt={group.eventName}
                      />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </main>
      </div>
    );
  }

  const roleContext = role === USER_ROLES.PHOTOGRAPHER
    ? {
        title: "Eventos vinculados",
        description: "Escolha um evento para acompanhar a cobertura e abrir a operação fotográfica.",
        context: "Escopo do fotógrafo",
        contextDetail: "Apenas eventos vinculados ao seu perfil",
        empty: "Nenhum evento está vinculado ao seu perfil.",
      }
    : {
        title: "Eventos",
        description: "Encontre o evento certo e abra sua operação de fotos em poucos passos.",
        context: "Catálogo operacional",
        contextDetail: "Todos os eventos ativos da sua empresa",
        empty: "Nenhum evento corresponde aos filtros atuais.",
      };

  return (
    <div className="pagina-cad cadastro-shell eventos-page">
      <MenuLateral />

      <main className="conteudo-cad">
        <CadastroPageHeader
          eyebrow="Operação / Eventos"
          title={roleContext.title}
          description={roleContext.description}
          icon={FiCalendar}
          context={roleContext.context}
          contextDetail={roleContext.contextDetail}
        />

        <form className="eventos-filter-panel" onSubmit={handleFilterSubmit}>
          <CadastroPanelHeading
            eyebrow="Encontrar contexto"
            title="Escolha um evento"
            description="Comece pelo nome ou refine a lista por situação e período. Sem filtros, todos os eventos do seu escopo aparecem aqui."
            icon={FiSliders}
          />

          <div className="eventos-filter-grid">
            <label className="eventos-filter-field eventos-filter-field-search" htmlFor="event-search">
              <span>Nome do evento</span>
              <div className="eventos-input-with-icon">
                <FiSearch aria-hidden="true" />
                <input
                  id="event-search"
                  type="search"
                  value={filters.search}
                  onChange={(event) => handleFilterChange("search", event.target.value)}
                  placeholder="Buscar por nome do evento"
                />
              </div>
            </label>

            <label className="eventos-filter-field" htmlFor="event-status">
              <span>Situação</span>
              <select
                id="event-status"
                value={filters.status}
                onChange={(event) => handleFilterChange("status", event.target.value)}
              >
                <option value="">Todas as situações</option>
                {Object.entries(EVENT_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <label className="eventos-filter-field" htmlFor="event-start-date">
              <span>A partir de</span>
              <input
                id="event-start-date"
                type="date"
                value={filters.startDate}
                onChange={(event) => handleFilterChange("startDate", event.target.value)}
              />
            </label>

            <label className="eventos-filter-field" htmlFor="event-end-date">
              <span>Até</span>
              <input
                id="event-end-date"
                type="date"
                value={filters.endDate}
                onChange={(event) => handleFilterChange("endDate", event.target.value)}
              />
            </label>

            <label className="eventos-filter-field eventos-filter-field-sort" htmlFor="event-sort">
              <span>Ordenar por</span>
              <select
                id="event-sort"
                value={filters.sort}
                onChange={(event) => handleFilterChange("sort", event.target.value)}
              >
                {Object.entries(SORT_OPTIONS).map(([value, option]) => (
                  <option key={value} value={value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="eventos-filter-footer">
            <span className="eventos-filter-summary">
              {activeFilterCount
                ? `${activeFilterCount} filtro(s) aplicado(s)`
                : "Todos os eventos do escopo"}
            </span>

            <div className="eventos-filter-actions">
              {activeFilterCount > 0 && (
                <button type="button" className="eventos-button eventos-button-secondary" onClick={clearFilters}>
                  Limpar filtros
                </button>
              )}
              <button type="submit" className="eventos-button eventos-button-primary" disabled={loading}>
                <FiSearch aria-hidden="true" />
                Buscar eventos
              </button>
            </div>
          </div>
        </form>

        {errorMessage && (
          <div className="eventos-alert" role="alert">
            <FiAlertCircle aria-hidden="true" />
            <span>{errorMessage}</span>
            <button type="button" onClick={() => setAppliedFilters({ ...appliedFilters })}>
              <FiRefreshCw aria-hidden="true" />
              Tentar novamente
            </button>
          </div>
        )}

        <section className="eventos-results" aria-labelledby="eventos-results-title">
          <div className="eventos-results-header">
            <div>
              <span className="eventos-results-kicker">Lista de trabalho</span>
              <h2 id="eventos-results-title">Eventos disponíveis</h2>
              <p>
                {loading
                  ? "Atualizando a lista…"
                  : pagination.total === 1
                    ? "1 evento encontrado"
                    : `${pagination.total} eventos encontrados`}
              </p>
            </div>

            <label className="eventos-page-size" htmlFor="event-page-size">
              <span>Por página</span>
              <select
                id="event-page-size"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="eventos-grid" role="status" aria-label="Carregando eventos">
              {Array.from({ length: 3 }, (_, index) => <EventCardSkeleton key={index} />)}
            </div>
          ) : eventos.length > 0 ? (
            <div className="eventos-grid">
              {eventos.map((event) => (
                <EventCard key={event.id} event={event} onOpen={openEvent} />
              ))}
            </div>
          ) : (
            <div className="eventos-empty-state" role="status">
              <FiCalendar aria-hidden="true" />
              <strong>{roleContext.empty}</strong>
              <span>
                {activeFilterCount
                  ? "Tente remover um filtro ou buscar por outro nome."
                  : "Quando houver eventos ativos, eles aparecerão nesta lista."}
              </span>
              {activeFilterCount > 0 && (
                <button type="button" className="eventos-button eventos-button-secondary" onClick={clearFilters}>
                  Limpar filtros
                </button>
              )}
            </div>
          )}

          {!loading && eventos.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={pagination.total}
              limit={pageSize}
              onPageChange={setPage}
            />
          )}
        </section>
      </main>
    </div>
  );
}

export default Eventos;
